// IndexedDB backend of the speech spool: database `poneva-speech-spool` v1.
//
//   sessions  keyPath `id`,                     index `owner`
//   chunks    keyPath [`sessionId`, `firstSeq`], index `sessionId`
//
// `openIdbSpoolBackend()` resolves null when IndexedDB is missing or refuses
// to open (private mode, blocked storage); the spool then uses memory.
import type { SpoolMeta } from '../contracts';
import type { SpoolBackend, StoredChunk } from './backend';

export const SPOOL_DB_NAME = 'poneva-speech-spool';
export const SPOOL_DB_VERSION = 1;
const SESSIONS = 'sessions';
const CHUNKS = 'chunks';
const OPEN_TIMEOUT_MS = 3_000;

const request = <T>(req: IDBRequest<T>): Promise<T> =>
  new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error('indexeddb request failed'));
  });

const done = (tx: IDBTransaction): Promise<void> =>
  new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onabort = () => reject(tx.error ?? new Error('indexeddb transaction aborted'));
    tx.onerror = () => reject(tx.error ?? new Error('indexeddb transaction failed'));
  });

function upgrade(db: IDBDatabase) {
  if (!db.objectStoreNames.contains(SESSIONS))
    db.createObjectStore(SESSIONS, { keyPath: 'id' }).createIndex('owner', 'owner');
  if (!db.objectStoreNames.contains(CHUNKS))
    db.createObjectStore(CHUNKS, { keyPath: ['sessionId', 'firstSeq'] }).createIndex(
      'sessionId',
      'sessionId',
    );
}

function openDatabase(factory: IDBFactory): Promise<IDBDatabase | null> {
  return new Promise((resolve) => {
    let settled = false;
    const finish = (db: IDBDatabase | null) => {
      if (settled) {
        db?.close();
        return;
      }
      settled = true;
      clearTimeout(timer);
      resolve(db);
    };
    const timer = setTimeout(() => finish(null), OPEN_TIMEOUT_MS);
    try {
      const req = factory.open(SPOOL_DB_NAME, SPOOL_DB_VERSION);
      req.onupgradeneeded = () => upgrade(req.result);
      req.onsuccess = () => {
        const db = req.result;
        // Another tab upgrading must not be blocked by this connection.
        db.onversionchange = () => db.close();
        finish(db);
      };
      req.onerror = () => finish(null);
      req.onblocked = () => finish(null);
    } catch {
      finish(null);
    }
  });
}

export async function openIdbSpoolBackend(
  factory: IDBFactory | undefined = globalThis.indexedDB,
): Promise<SpoolBackend | null> {
  if (!factory) return null;
  const db = await openDatabase(factory);
  if (!db) return null;
  const store = (name: string, mode: IDBTransactionMode) => {
    const tx = db.transaction(name, mode);
    return { tx, store: tx.objectStore(name) };
  };
  const chunkRange = (id: string, from: number, to: number) =>
    IDBKeyRange.bound([id, from], [id, to]);
  return {
    async putSession(meta, pairedChunks = []) {
      const tx = db.transaction(pairedChunks.length ? [SESSIONS, CHUNKS] : SESSIONS, 'readwrite');
      const committed = done(tx);
      try {
        tx.objectStore(SESSIONS).put(meta);
        for (const chunk of pairedChunks) tx.objectStore(CHUNKS).put(chunk);
      } catch (error) {
        tx.abort();
        await committed.catch(() => {});
        throw error;
      }
      await committed;
    },
    async getSession(id) {
      const { store: sessions } = store(SESSIONS, 'readonly');
      return ((await request(sessions.get(id))) as SpoolMeta | undefined) ?? null;
    },
    async listSessions(owner) {
      const { store: sessions } = store(SESSIONS, 'readonly');
      const rows =
        owner === undefined
          ? await request(sessions.getAll())
          : await request(sessions.index('owner').getAll(owner));
      return rows as SpoolMeta[];
    },
    async deleteSession(id) {
      const tx = db.transaction([SESSIONS, CHUNKS], 'readwrite');
      tx.objectStore(SESSIONS).delete(id);
      tx.objectStore(CHUNKS).delete(chunkRange(id, -Infinity, Infinity));
      await done(tx);
    },
    async putChunk(chunk) {
      const { tx, store: chunks } = store(CHUNKS, 'readwrite');
      chunks.put(chunk);
      await done(tx);
    },
    async deleteChunk(sessionId, firstSeq) {
      const { tx, store: chunks } = store(CHUNKS, 'readwrite');
      chunks.delete([sessionId, firstSeq]);
      await done(tx);
    },
    async readChunks(sessionId, fromFirstSeq, toFirstSeq, limit) {
      const { store: chunks } = store(CHUNKS, 'readonly');
      const range = chunkRange(sessionId, fromFirstSeq, toFirstSeq);
      return (await request(
        limit === undefined ? chunks.getAll(range) : chunks.getAll(range, limit),
      )) as StoredChunk[];
    },
  };
}
